import { memo, useCallback } from 'react';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { editAndRelayoutText } from '../../state/actions/edit-and-relayout-text';
import { useWriteContext } from '../../utils/use-context';
import { NumberControlUpdateHandler } from './number-controls';
import { useTranslations } from 'next-intl';
import { FormatLetterSpacing2 } from '@crabs-ai/web-icons';
import { ValueInputField } from '@crabs-ai/ds-react';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { useSelectionValues } from '../utils/use-selection-values';
import { TextItem } from '../../items/text/text-item-type';
import { CaptionsItem } from '../../items/captions/captions-item-type';

const LETTER_SPACING_MIN = -10;
const LETTER_SPACING_MAX = 50;
const LETTER_SPACING_STEP = 0.2;

const LetterSpacingControlsUnmemoized: React.FC<{
  letterSpacing: number;
  itemId: string;
}> = ({ letterSpacing, itemId }) => {
  const t = useTranslations();

  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);
  const { isMixed } = useSelectionValues<TextItem | CaptionsItem, number>(
    itemId,
    (item) => item.letterSpacing,
  );

  const onLetterSpacingChange: NumberControlUpdateHandler = useCallback(
    ({ num, commitToUndoStack }) => {
      // letterSpacing이 적절한 범위 내에 있는지 확인
      const validLetterSpacing = Math.max(
        LETTER_SPACING_MIN,
        Math.min(LETTER_SPACING_MAX, num),
      );

      setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            if (i.type === 'text') {
              return editAndRelayoutText(i, () => {
                if (i.letterSpacing === validLetterSpacing) {
                  return i;
                }

                return {
                  ...i,
                  letterSpacing: validLetterSpacing,
                };
              });
            }

            if (i.type === 'captions') {
              return {
                ...i,
                letterSpacing: validLetterSpacing,
              };
            }

            throw new Error(
              'Letter spacing can only be changed for text and captions items',
            );
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds],
  );

  return (
    <div className="flex-1">
      <ValueInputField
        isShowLabel={true}
        label={t('Editor.inspector.text.letter-spacing')}
        isShowLeadingNode={true}
        leadingNodeType="icon"
        leadingNodeValue={
          <FormatLetterSpacing2 className="size-[20px] [&_path]:fill-sys-cont-neutral-default" />
        }
        setValue={onLetterSpacingChange}
        value={letterSpacing}
        min={LETTER_SPACING_MIN}
        max={LETTER_SPACING_MAX}
        step={LETTER_SPACING_STEP}
        accessibilityLabel="Letter spacing"
        hasMixedValue={isMixed}
        className="w-full"
      />
    </div>
  );
};

export const LetterSpacingControls = memo(LetterSpacingControlsUnmemoized);
